import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  isMemberLoggedIn,
  isVendorLoggedIn,
  removeLoggedInMemberDetails,
} from "./utils/authUtils";
import { vendorSessionExpiredRequest } from "./redux/vendor/auth/logout/action";
import Home from "./pages/Admin/Dashboard/Home";
import "./App.scss";
import UserView from "./pages/Admin/Users/Users";
import Login from "./pages/Admin/Auth/Login/Login";
import Forgot from "./pages/Admin/Auth/Forgot/Forgot";
import ResetPassword from "./pages/Admin/Auth/Forgot/ResetPassword";
import Layout from "./components/Common/Layout/Layout";
import PageNotFound404 from "./components/NotFound/PageNotFound404";
import AddPromotion from "./pages/Admin/Promotion/AddPromotion";
import ViewPromotion from "./pages/Admin/Promotion/ViewPromotion";
import MemberList from "./pages/Admin/Members/MemberList";
import ViewMember from "./pages/Admin/Members/ViewMember";
import InquiryList from "./pages/Admin/Inquiry/InquiryList";
import PromotionList from "./pages/Admin/Promotion/PromotionList";
import InquiryForm from "./pages/Frontend/Inquiry/InquiryForm";
import LoginPage from "./pages/Frontend/Auth/LoginPage";
import ForgotPassword from "./pages/Frontend/Auth/ForgotPassword";
import ForgotLogin from "./pages/Frontend/Auth/ForgotLogin";
import HomePage from "./pages/Frontend/Home/HomePage";
import PromotionPage from "./pages/Frontend/Promotion/Promotion";
import ResetPasswordMember from "./pages/Frontend/Auth/ResetPasswordMember";
import LayoutMember from "./components/Common/Frontend/Layout/Layout";
import ProfileView from "./pages/Frontend/Profile/ProfileView";
import SupportPage from "./pages/Frontend/Support/Support";
import PromotionDetailsPage from "./pages/Frontend/Promotion/PromotionDetails";
import AgreementPage from "./pages/Frontend/Agreement/Agreement";
import NewPromotions from "./pages/Frontend/ViewPromotion/NewPromotions";
import ExpireSoon from "./pages/Frontend/ViewPromotion/ExpireSoon";
import AllPromotion from "./pages/Frontend/ViewPromotion/AllPromotion";
import AddAchManagement from "./pages/Frontend/AchManagement/AddAchManagement";
import ViewAchManagement from "./pages/Frontend/AchManagement/ViewAchManagement";
import AchManagement from "./pages/Admin/Ach/AchManagement";
import Rebate from "./pages/Frontend/Rebate/Rebate";
import MyPromotionPage from "./pages/Frontend/MyPromotion/MyPromotionPage";
import RebateList from "./pages/Admin/Rebate/RebateList";
import NewsLetter from "./pages/Admin/NewsLetter/NewsLetter";
import AddNewsLetter from "./pages/Admin/NewsLetter/AddNewsLetter";
import NewsLetterPage from "./pages/Frontend/Newsletter/NewsLetter";
import NewsLetterDetail from "./pages/Frontend/Newsletter/NewsLetterDetail";
import NewsLetterDetailPage from "./pages/Admin/NewsLetter/NewsLetterDetail";
import AddVendor from "./pages/Admin/Vendor/AddVendor";
import VendorProgram from "./pages/Frontend/VendorProgram/VendorProgram";
import VendorProgramDetails from "./pages/Frontend/VendorProgram/VendorProgramDetails";
import ViewVenderDetails from "./pages/Admin/Vendor/ViewVenderDetails";
import IndexVender from "./pages/Admin/Vendor/IndexVender";
import IndexZohoVender from "./pages/Admin/Vendor/ZohoVendors/IndexZohoVender";
import VendorUsers from "./pages/Admin/Vendor/ZohoVendors/VendorUsers";
import VendorLogsPage from "./pages/Admin/Vendor/ZohoVendors/VendorLogs";
import UserLogsPage from "./pages/Admin/Users/UserLogs";
import SupportList from "./pages/Admin/SupportRequest/SupportList";
import VendorContacts from "./pages/Admin/VendorContacts/VendorContacts";
import AddVendorContact from "./pages/Admin/VendorContacts/AddContact";
import UserGuide from "./pages/Frontend/UserGuide/UserGuide";
import ViewAchDetails from "./pages/Admin/Ach/ViewAchDetails";
import SupportDetails from "./pages/Admin/SupportDetails";
import LinkManagementList from "./pages/Admin/LinkManagement/LinkManagementList";
import DocumentManagement from "./pages/Admin/DocumentManagement/DocumentManagement";
import { updateFaviconAndManifest } from "./helper/menifestHelper";
import MembershipForm from "./pages/Frontend/Membership/MembershipForm";
import VendorLogin from "./pages/Vendor/Auth/login";
import VendorForgotPassword from "./pages/Vendor/Auth/ForgotPassword";
import VendorResetPassword from "./pages/Vendor/Auth/ResetPassword";
import VendorLayout from "./components/Common/Vendor/Layout/Layout";
import VendorDashboard from "./pages/Vendor/Dashboard/Dashboard";
import VendorMembers from "./pages/Vendor/Members/Members";
import VendorViewMember from "./pages/Vendor/Members/ViewMember";
import VendorSupportTickets from "./pages/Vendor/SupportTickets/SupportTickets";
import VendorDocuments from "./pages/Vendor/Documents/Documents";
import VendorChangePasswordSplit from "./pages/Vendor/ChangePassword/ChangePasswordSplit";

/* The guards live out here, not inside App. Defined in the body they were a new
 * component type on every App render - and App re-renders on every navigation,
 * because it reads useLocation - so react threw away the whole routed subtree
 * and mounted it again each time the url changed. Any state a page held (a
 * selected ticket, a filter, a half typed form) died with it. */

/* the admin area's own login, not /home - that used to be a public page and
 * is now behind MemberRoute, so a signed out admin would be bounced through
 * the member login instead */
const PrivateRoute = () => {
  return localStorage.getItem("user") ? (
    <Outlet />
  ) : (
    <Navigate to="/admin/login" replace />
  );
};

/* A vendor token is good for 24h. Checking only that the key exists would
 * keep an expired session inside the portal until some API call happened to
 * 401 - isVendorLoggedIn() reads the token's own exp instead. */
const VendorRoute = () => {
  const dispatch = useDispatch();
  const hasToken = Boolean(localStorage.getItem("vendorAccessToken"));
  const loggedIn = isVendorLoggedIn();

  useEffect(() => {
    /* a token that is present but past its exp is a session that ended
     * without a Logout click - close the trail before dropping it */
    if (hasToken && !loggedIn) {
      dispatch(vendorSessionExpiredRequest());
    }
  }, [hasToken, loggedIn]);

  return loggedIn ? <Outlet /> : <Navigate to="/vendor/login" />;
};

/* /home lives inside this guard, so sending a signed out member to /home only
 * re-entered the guard - the redirect was a no-op against itself and the page
 * rendered anyway. The member login is the way out.
 * hraMemberId alone is not proof of a session either: it survives an expired
 * token, so the member token and its exp are what decide. */
const MemberRoute = () => {
  if (!isMemberLoggedIn()) {
    removeLoggedInMemberDetails();
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
};

/* "/vendor" and "/vendor/:id" used to be the member's vendor list and detail,
 * now "/vendor-program". Installed PWAs, history and bookmarks still open the
 * old urls, so a signed in member is sent to the new page instead of landing
 * on the vendor portal's login. */
const VendorPrefixRoute = () => {
  const { id } = useParams();
  if (isVendorLoggedIn()) {
    return <Navigate to="/vendor/dashboard" replace />;
  }
  if (isMemberLoggedIn()) {
    return (
      <Navigate to={id ? `/vendor-program/${id}` : "/vendor-program"} replace />
    );
  }
  /* a member whose token expired still leaves hraMemberId behind - this url
   * was theirs, so their own login is the way back, not the vendor one */
  if (
    !localStorage.getItem("vendorAccessToken") &&
    (localStorage.getItem("hraMemberId") ||
      localStorage.getItem("memberAccessToken"))
  ) {
    removeLoggedInMemberDetails();
    return <Navigate to="/login" replace />;
  }
  return <Navigate to="/vendor/login" replace />;
};

/* the admin vendor screens live under "/admin/vendor/view/:id", so a bare
 * "/admin/vendor/:id" is sent to that vendor's detail page rather than the
 * 404. A signed out admin goes to the admin login. */
const AdminVendorPrefixRoute = () => {
  const { id } = useParams();
  if (!localStorage.getItem("user")) {
    return <Navigate to="/admin/login" replace />;
  }
  return <Navigate to={`/admin/vendor/view/${id}`} replace />;
};

function App() {
  const location = useLocation();

  useEffect(() => {
    const isAdmin = location.pathname.startsWith("/admin");
    updateFaviconAndManifest(isAdmin);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<PrivateRoute />}>
        <Route element={<Layout />}>
          <Route path="/admin/home" element={<Home />} />
          <Route path="/admin/user" element={<UserView />} />
          <Route path="/admin/user-logs" element={<UserLogsPage />} />
          <Route
            path="/admin/vendor-contact/add"
            element={<AddVendorContact />}
          />
          <Route
            path="/admin/vendor-contact/edit/:id"
            element={<AddVendorContact />}
          />
          <Route path="/admin/vendor-contact" element={<VendorContacts />} />
          <Route path="/admin/promotion" element={<PromotionList />} />
          <Route path="/admin/promotion/add" element={<AddPromotion />} />
          <Route path="/admin/promotion/edit/:id" element={<AddPromotion />} />
          <Route path="/admin/promotion/view/:id" element={<ViewPromotion />} />
          <Route path="/admin/members" element={<MemberList />} />
          <Route path="/admin/members/view/:id" element={<ViewMember />} />
          <Route path="/admin/rebate" element={<RebateList />} />
          <Route path="/admin/newsletter" element={<NewsLetter />} />
          <Route
            path="/admin/newsletter/:id"
            element={<NewsLetterDetailPage />}
          />
          <Route path="/admin/newsletter/add" element={<AddNewsLetter />} />
          <Route
            path="/admin/newsletter/edit/:id"
            element={<AddNewsLetter />}
          />
          <Route path="/admin/inquiry" element={<InquiryList />} />
          <Route path="/admin/vendor" element={<IndexVender />} />
          <Route path="/admin/vendor-list" element={<IndexZohoVender />} />
          <Route
            path="/admin/document-management"
            element={<DocumentManagement />}
          />
          <Route path="/admin/vendor/add" element={<AddVendor />} />
          <Route path="/admin/vendor/edit/:id" element={<AddVendor />} />
          <Route path="/admin/support-details" element={<SupportDetails />} />
          <Route
            path="/admin/link-management"
            element={<LinkManagementList />}
          />
          <Route
            path="/admin/vendor/view/:id"
            element={<ViewVenderDetails />}
          />
          <Route path="/admin/vendor-users/:id" element={<VendorUsers />} />
          {/* the vendor activity trail - a page rather than a dialog, it has a
              toolbar and paging of its own */}
          <Route path="/admin/vendor-logs/:id" element={<VendorLogsPage />} />
          <Route path="/admin/ach" element={<AchManagement />} />
          <Route path="/admin/ach/:id" element={<ViewAchDetails />} />
          <Route path="/admin/support" element={<SupportList />} />
        </Route>
      </Route>
      <Route element={<LayoutMember />}>
        <Route path="/" element={<MemberRoute />}>
          <Route path="/profile" element={<ProfileView />} />
          <Route path="/home" element={<HomePage />} />
          <Route
            path="/member-application-user-guide"
            element={<UserGuide />}
          />

          <Route path="/promotion/latest" element={<NewPromotions />} />
          <Route path="/promotion/expiresoon" element={<ExpireSoon />} />
          <Route path="/promotion/all" element={<AllPromotion />} />
          <Route
            path="/promotion/view/:id"
            element={<PromotionDetailsPage />}
          />
          <Route path="/promotion" element={<PromotionPage />} />
          <Route path="/myPromotions" element={<MyPromotionPage />} />
          <Route path="/ach/add" element={<AddAchManagement />} />
          <Route path="/ach/view" element={<ViewAchManagement />} />
          <Route path="/ach/edit" element={<AddAchManagement />} />
          <Route path="/newsletter" element={<NewsLetterPage />} />
          <Route path="/newsletter/:id" element={<NewsLetterDetail />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/agreement" element={<AgreementPage />} />
          <Route path="/rebate" element={<Rebate />} />
          <Route path="/vendor-program" element={<VendorProgram />} />
          <Route
            path="/vendor-program/:id"
            element={<VendorProgramDetails />}
          />
        </Route>
      </Route>

      
      {/* frontend */}
      <Route index path="/" element={<LoginPage />} />
      <Route index path="/login" element={<LoginPage />} />
      <Route path="/forgotlogin" element={<ForgotLogin />} />
      <Route path="/inquiry" element={<InquiryForm />} />
      {/* <Route path="/membership" element={<MembershipForm />} /> */}
      <Route path="/forgotPassword" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPasswordMember />} />
      {/* frontend */}
      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin/forgot" element={<Forgot />} />
      <Route
        path="/admin/reset-password/:id/:email"
        element={<ResetPassword />}
      />
      {/* "/admin/vendor/add" and the other static admin vendor routes outrank
          "/admin/vendor/:id" */}
      <Route path="/admin/vendor/:id" element={<AdminVendorPrefixRoute />} />
      <Route path="*" element={<PageNotFound404 />} />

      {/* vendor portal. The member-facing vendor list lives under
          "/vendor-program", so nothing here shares its prefix. */}
      {/* the prefix on its own is not a screen - send it to the portal's front
          door rather than to the 404, or a member on an old url to
          "/vendor-program". The static routes below outrank "/vendor/:id". */}
      <Route path="/vendor" element={<VendorPrefixRoute />} />
      <Route path="/vendor/:id" element={<VendorPrefixRoute />} />
      <Route path="/vendor/login" element={<VendorLogin />} />
      <Route path="/vendor/forgot-password" element={<VendorForgotPassword />} />
      <Route path="/vendor/reset-password/:token/:email" element={<VendorResetPassword />} />
      <Route element={<VendorRoute />}>
        <Route element={<VendorLayout />}>
          <Route path="/vendor/dashboard" element={<VendorDashboard />} />
          <Route path="/vendor/members" element={<VendorMembers />} />
          <Route path="/vendor/members/view/:id" element={<VendorViewMember />} />
          <Route path="/vendor/support-tickets" element={<VendorSupportTickets />} />
          <Route path="/vendor/documents" element={<VendorDocuments />} />
          {/* reached from the avatar menu, not from the nav - it is an account
              screen rather than one of the portal's four sections */}
          <Route
            path="/vendor/change-password"
            element={<VendorChangePasswordSplit />}
          />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
